// Scoring screens: start a round, then a hole-by-hole scorecard. Everything works with no
// signal; the status line says what's waiting to sync. Big targets: this is used on the course,
// one-handed, often with a glove on.

import type { Course } from "@union/contracts";
import { Badge } from "@union/ui/components/badge";
import { Button } from "@union/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@union/ui/components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@union/ui/components/table";
import { apiGet } from "@union/web-kit";
import { ErrorState } from "@union/web-kit/ui";
import { Check, CloudOff, Flag, Loader2, Minus, Plus, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { signInAgain, useClubSession } from "../club-session.ts";
import {
  cacheCourses,
  cachedCourses,
  getRound,
  type LocalRound,
  listRounds,
  pendingHoles,
  putRound,
  recordScore,
} from "./store.ts";
import { type SyncState, syncRound } from "./sync.ts";

export function StartRound({ slug, go }: { slug: string; go: (path: string) => void }) {
  const session = useClubSession();
  const [courses, setCourses] = useState<Course[] | undefined>();
  const [rounds, setRounds] = useState<LocalRound[]>([]);
  const [error, setError] = useState<unknown>();

  const load = useCallback(async () => {
    setError(undefined);
    setRounds(await listRounds(slug));
    const cached = await cachedCourses(slug);
    if (cached) setCourses(cached);
    if (!session) return;
    try {
      const fresh = await apiGet<Course[]>(`/member/clubs/${slug}/courses`, session);
      await cacheCourses(slug, fresh);
      setCourses(fresh);
    } catch (e) {
      if (!cached) setError(e); // offline is fine if we've seen the courses before
    }
  }, [slug, session]);
  useEffect(() => {
    load();
  }, [load]);

  async function start(course: Course) {
    const roundId = crypto.randomUUID(); // created on the device: works offline
    await putRound({
      roundId,
      clubSlug: slug,
      course,
      startedAt: new Date().toISOString(),
      scores: {},
      confirmed: {},
    });
    go(`/score/${roundId}`);
  }

  return (
    <>
      <div className="grid gap-space-1">
        <h1 className="type-display-l max-sm:type-heading">Score a round</h1>
        <p className="type-body text-muted-foreground">
          Scores save on this phone and sync when there’s signal.
        </p>
      </div>
      {error !== undefined && (
        <ErrorState what="the courses" error={error} onRetry={load} onSignIn={() => signInAgain(slug)} />
      )}
      <div className="grid gap-space-3 sm:grid-cols-2">
        {courses?.map((course) => (
          <Card key={course.id}>
            <CardHeader>
              <CardTitle>{course.name}</CardTitle>
              <CardDescription>
                {course.holes.length} holes · par {course.holes.reduce((n, h) => n + h.par, 0)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => start(course)}>
                <Flag /> Start a round on {course.name}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {rounds.length > 0 && (
        <section className="grid gap-space-2" aria-labelledby="rounds-heading">
          <h2 id="rounds-heading" className="type-title">
            Your rounds on this device
          </h2>
          <Card className="gap-0 overflow-hidden py-0">
            {rounds.map((r) => (
              <a
                key={r.roundId}
                href={`#/score/${r.roundId}`}
                className="flex min-h-row-comfy items-center gap-space-3 border-b border-border px-space-4 last:border-b-0 even:bg-stripe hover:bg-muted"
              >
                <span className="flex-1 type-body">
                  {r.course.name}, {new Date(r.startedAt).toLocaleDateString()}
                </span>
                {pendingHoles(r).length > 0 && (
                  <Badge variant="warning">
                    <CloudOff /> Waiting to sync
                  </Badge>
                )}
              </a>
            ))}
          </Card>
        </section>
      )}
    </>
  );
}

const STATUS: Record<SyncState, (pending: number) => string> = {
  synced: () => "All scores saved to Union",
  pending: (n) => `Saved on this device · ${n} ${n === 1 ? "hole" : "holes"} syncing…`,
  offline: (n) =>
    `No signal · ${n} ${n === 1 ? "hole" : "holes"} saved on this device, will sync automatically`,
  error: (n) => `Couldn’t sync ${n} ${n === 1 ? "hole" : "holes"} yet · saved on this device, retrying`,
};

const STATUS_LOOK: Record<SyncState, { variant: "success" | "neutral" | "warning"; icon: typeof Check }> = {
  synced: { variant: "success", icon: Check },
  pending: { variant: "neutral", icon: Loader2 },
  offline: { variant: "warning", icon: CloudOff },
  error: { variant: "warning", icon: TriangleAlert },
};

export function ScorecardScreen({ roundId }: { roundId: string }) {
  const session = useClubSession();
  const accessToken = session?.access_token;
  const [round, setRound] = useState<LocalRound | null | undefined>();
  const [state, setState] = useState<SyncState>("synced");

  const sync = useCallback(async () => {
    const result = await syncRound(roundId, accessToken);
    if (result.round) setRound(result.round);
    setState(result.state);
  }, [roundId, accessToken]);

  useEffect(() => {
    getRound(roundId).then((r) => setRound(r ?? null));
    sync();
    // Sync when signal returns, and keep trying while anything is waiting.
    window.addEventListener("online", sync);
    const timer = window.setInterval(sync, 15_000);
    return () => {
      window.removeEventListener("online", sync);
      window.clearInterval(timer);
    };
  }, [roundId, sync]);

  async function tap(hole: number, par: number, delta: number) {
    const current = round?.scores[hole]?.strokes;
    const next = Math.min(20, Math.max(1, (current ?? par) + (current === undefined ? 0 : delta)));
    setRound(await recordScore(roundId, hole, next));
    setState("pending");
    sync();
  }

  if (round === undefined) return null;
  if (round === null)
    return <p className="type-body text-muted-foreground">This round isn’t on this device.</p>;

  const played = round.course.holes.filter((h) => round.scores[h.number]);
  const total = played.reduce((n, h) => n + (round.scores[h.number]?.strokes ?? 0), 0);
  const toPar = played.reduce((n, h) => n + (round.scores[h.number]?.strokes ?? 0) - h.par, 0);
  const pending = pendingHoles(round).length;
  const shown: SyncState = pending === 0 ? "synced" : state === "synced" ? "pending" : state;
  const look = STATUS_LOOK[shown];

  return (
    <>
      <div className="grid gap-space-2">
        <h1 className="type-display-l max-sm:type-heading">{round.course.name}</h1>
        <Badge variant={look.variant} role="status" data-testid="round-status" className="whitespace-normal">
          <look.icon
            className={shown === "pending" ? "animate-spin motion-reduce:animate-none" : undefined}
          />
          {STATUS[shown](pending)}
        </Badge>
      </div>
      <Card className="flex-row items-baseline gap-space-6 px-space-5 py-space-4" data-testid="round-totals">
        <div>
          <p className="type-eyebrow text-muted-foreground">Holes</p>
          <p className="type-score">{played.length}</p>
        </div>
        <div>
          <p className="type-eyebrow text-muted-foreground">Strokes</p>
          <p className="type-score">{total}</p>
        </div>
        <div>
          <p className="type-eyebrow text-muted-foreground">To par</p>
          <p className="type-score">{toPar === 0 ? "E" : toPar > 0 ? `+${toPar}` : toPar}</p>
        </div>
      </Card>
      <Table aria-label="Scorecard" data-testid="scorecard">
        <TableHeader>
          <TableRow>
            <TableHead>Hole</TableHead>
            <TableHead>Par</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>
              <span className="sr-only">Change score</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {round.course.holes.map((h) => {
            const score = round.scores[h.number];
            const unsynced = score && round.confirmed[h.number] !== score.recordedAt;
            return (
              <TableRow key={h.number}>
                <TableCell className="h-row-comfy type-time">{h.number}</TableCell>
                <TableCell className="type-time text-muted-foreground">{h.par}</TableCell>
                <TableCell className="type-time">
                  {score ? score.strokes : "—"}
                  {unsynced && (
                    <span className="ml-space-1 text-warning" title="Not synced yet">
                      •
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-space-2">
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={`Hole ${h.number} minus one`}
                      disabled={!score}
                      onClick={() => tap(h.number, h.par, -1)}
                    >
                      <Minus />
                    </Button>
                    <Button
                      variant={score ? "outline" : "primary"}
                      className="min-w-tap-min px-space-3"
                      aria-label={`Hole ${h.number} plus one`}
                      onClick={() => tap(h.number, h.par, +1)}
                    >
                      {score ? <Plus /> : "Par"}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}
